import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Minus, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { eur, scrollToId } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_SHIPPING_AT = 50;
const SHIPPING_FEE = 2.99;

export default function CartDrawer() {
    const { items, total, count, open, setOpen, removeItem, setQty } = useCart();
    const shipping = items.length === 0 || total >= FREE_SHIPPING_AT ? 0 : SHIPPING_FEE;
    const [cgv, setCgv] = useState(false);
    const [loading, setLoading] = useState(false);

    const checkout = async () => {
        if (!items.length) return;
        if (!cgv) {
            toast.error("Accepte les Conditions Générales de Vente pour continuer");
            return;
        }
        try {
            setLoading(true);
            const res = await fetch(`${API}/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    origin_url: window.location.origin,
                    items: items.map((i) => ({ lookup_key: i.lookup_key, size: i.size, quantity: i.qty })),
                }),
            });
            const data = await res.json();
            if (!data.checkout_url) throw new Error(data.detail || "Erreur de paiement");
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error("Impossible de démarrer le paiement. Réessaie.");
            setLoading(false);
        }
    };

    const goToCollection = () => {
        setOpen(false);
        scrollToId("products");
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
                        data-testid="cart-backdrop"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-spidey-card border-l border-zinc-800 flex flex-col"
                        data-testid="cart-drawer"
                    >
                        <div className="flex items-center justify-between px-6 h-16 border-b border-zinc-800/80 shrink-0">
                            <p className="font-display uppercase text-lg text-white tracking-wide">
                                Ton panier{" "}
                                <span className="text-spidey-neon" data-testid="cart-count">
                                    ({count})
                                </span>
                            </p>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Fermer le panier"
                                className="text-zinc-500 hover:text-white transition-colors p-1"
                                data-testid="cart-close-button"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {items.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                                <ShoppingBag size={40} className="text-zinc-700" />
                                <p className="mt-4 text-sm text-zinc-500" data-testid="cart-empty-message">
                                    Ton panier est vide. Les deux paires Spider-Web t'attendent.
                                </p>
                                <button
                                    onClick={goToCollection}
                                    className="mt-6 bg-spidey-red hover:bg-spidey-neon text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-full transition-all hover:shadow-[0_0_25px_rgba(230,0,38,0.5)]"
                                    data-testid="cart-browse-button"
                                >
                                    Découvrir la collection
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-y-auto px-6" data-testid="cart-items">
                                    {items.map((i, idx) => (
                                        <div key={i.key} className="flex gap-4 py-5 border-b border-zinc-800/60" data-testid={`cart-item-${idx}`}>
                                            <img
                                                src={i.image}
                                                alt={i.name}
                                                className="w-16 h-16 rounded-xl object-cover border border-zinc-800 shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white font-medium truncate">{i.name}</p>
                                                <p className="mt-0.5 text-xs text-zinc-500 font-mono-brand">{i.size}</p>
                                                <div className="mt-2.5 inline-flex items-center border border-zinc-800 rounded-full">
                                                    <button
                                                        onClick={() => setQty(i.key, i.qty - 1)}
                                                        aria-label="Réduire la quantité"
                                                        className="p-1.5 px-2 text-zinc-400 hover:text-white transition-colors"
                                                        data-testid={`cart-qty-minus-${idx}`}
                                                    >
                                                        <Minus size={13} />
                                                    </button>
                                                    <span className="w-6 text-center text-xs text-white font-mono-brand" data-testid={`cart-qty-${idx}`}>
                                                        {i.qty}
                                                    </span>
                                                    <button
                                                        onClick={() => setQty(i.key, i.qty + 1)}
                                                        aria-label="Augmenter la quantité"
                                                        className="p-1.5 px-2 text-zinc-400 hover:text-white transition-colors"
                                                        data-testid={`cart-qty-plus-${idx}`}
                                                    >
                                                        <Plus size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end justify-between shrink-0">
                                                <p className="text-sm text-white font-bold" data-testid={`cart-item-price-${idx}`}>
                                                    {eur(i.price * i.qty)}
                                                </p>
                                                <button
                                                    onClick={() => removeItem(i.key)}
                                                    aria-label="Retirer l'article"
                                                    className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                                                    data-testid={`cart-remove-${idx}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="shrink-0 border-t border-zinc-800/80 px-6 py-5 bg-black/30">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-mono-brand uppercase tracking-[0.18em] text-zinc-500">
                                            Sous-total
                                        </span>
                                        <span className="font-display text-2xl text-white" data-testid="cart-subtotal">
                                            {eur(total)}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 flex items-center justify-between">
                                        <span className="text-xs font-mono-brand uppercase tracking-[0.18em] text-zinc-500">
                                            Livraison
                                        </span>
                                        <span
                                            className={`text-sm font-bold ${shipping === 0 ? "text-emerald-400" : "text-white"}`}
                                            data-testid="cart-shipping"
                                        >
                                            {shipping === 0 ? "Offerte" : eur(shipping)}
                                        </span>
                                    </div>
                                    <div className="mt-3" data-testid="cart-shipping-progress">
                                        <p
                                            className={`flex items-center gap-1.5 text-[11px] ${shipping === 0 ? "text-emerald-400" : "text-spidey-neon"}`}
                                            data-testid="cart-free-shipping-hint"
                                        >
                                            {shipping === 0 ? (
                                                <>
                                                    <Check size={12} />
                                                    Livraison offerte débloquée
                                                </>
                                            ) : (
                                                <>
                                                    <Truck size={12} />
                                                    Plus que {eur(FREE_SHIPPING_AT - total)} pour la livraison offerte
                                                </>
                                            )}
                                        </p>
                                        <div className="mt-2 h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden">
                                            <motion.div
                                                className={`h-full rounded-full ${shipping === 0 ? "bg-emerald-400" : "bg-gradient-to-r from-spidey-red to-spidey-neon"}`}
                                                initial={false}
                                                animate={{ width: `${Math.min((total / FREE_SHIPPING_AT) * 100, 100)}%` }}
                                                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                                data-testid="cart-shipping-progress-bar"
                                            />
                                        </div>
                                        {shipping > 0 && (
                                            <button
                                                onClick={goToCollection}
                                                className="mt-2 text-[11px] text-zinc-500 hover:text-spidey-neon transition-colors underline underline-offset-2"
                                                data-testid="cart-duo-hint"
                                            >
                                                Astuce : le Duo (89,99 €) débloque la livraison offerte à lui tout seul
                                            </button>
                                        )}
                                    </div>

                                    <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={cgv}
                                            onChange={(e) => setCgv(e.target.checked)}
                                            className="mt-0.5 w-4 h-4 accent-[#E60026]"
                                            data-testid="cart-cgv-checkbox"
                                        />
                                        <span className="text-xs text-zinc-500 leading-relaxed">
                                            J'ai lu et j'accepte les{" "}
                                            <a
                                                href="/infos/cgv"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-spidey-neon underline underline-offset-2"
                                            >
                                                Conditions Générales de Vente
                                            </a>
                                        </span>
                                    </label>

                                    <button
                                        onClick={checkout}
                                        disabled={loading}
                                        className="mt-4 w-full flex items-center justify-center gap-2 bg-spidey-red hover:bg-spidey-neon disabled:opacity-70 text-white font-bold uppercase tracking-wider text-sm py-4 rounded-full transition-all hover:shadow-[0_0_30px_rgba(230,0,38,0.5)]"
                                        data-testid="cart-checkout-button"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Redirection vers le paiement...
                                            </>
                                        ) : (
                                            `Commander — ${eur(total + shipping)}`
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
